<!-- eslint-disable i18next/no-literal-string -->
<template>
  <div class="mc-videohistory-list__container" :class="{ 'no-filter': !vehicleId }">
    <template v-if="vehicleId">
      <div class="mc-videhistory-list__header-wrapper" :class="{ scrolled: sidebarScrolled }">
        <div class="mc-videohistory-list__vehicle-info">
          <template v-if="loadingVehicle">
            <b-skeleton width="25%" />
            <b-skeleton />
          </template>
          <template v-else>
            <div class="mc-videohistory-list__header-info-wrapper">
              <div class="mc-videohistory-list__header-info-vehicle">
                <h1>{{ vehicleDetail?.licensePlate }}</h1>
                <p>
                  {{
                    vehicleDetail?.vehicleType +
                    ' / ' +
                    vehicleDetail?.vehicleCategory +
                    ' / ' +
                    vehicleDetail?.manufacturer
                  }}
                </p>
              </div>
              <div
                v-if="
                  (hasAccess(['b3j-trackvision-video-history-status:read']) &&
                    !isHasAccessSwitchView) ||
                  (isHasAccessSwitchView && isNewView)
                "
                class="mc-videohistory-list__header-video-status-wrapper"
              >
                <b-skeleton v-if="$store.state.vehicle.loadingVideoStatus" />
                <template v-else>
                  <VideoStatusBadge v-if="showBadgeVideoStatus" :isOnline="isVideoStatusOnline" />
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="mc-videohistory-list__scroll-wrapper" @scroll="handleScrollSidebar">
        <div class="mc-videohistory-list__periode">
          <h4>{{ $t('videoHistory.videoPeriod') }}</h4>
          <p>{{ periodActive }}</p>
        </div>
        <div class="mc-videohistory-list__btn-filter-wrapper">
          <McButton
            :text="$t('videoHistory.updateFilter')"
            variant="outline-primary"
            :icon="'icon-outline_filter'"
            :disabled="$store.state.vehicle.isLoadingVehicleVideos"
            @click="openModalFilter"
          />
        </div>
        <div class="mc-videohistory-list__divider" />
        <HistoryListV2 @sendOnlineCommandWakeup="sendOnlineCommandWakeup" />
      </div>
    </template>
    <template v-else>
      <div class="mc-videohistory-list__no-filter-wrapper">
        <img
          src="@/assets/images/nofilter.png"
          alt="no-filter"
          class="mc-videohistory-list__no-filter-illustration"
        />
        <div class="mc-videohistory-list__no-filter-content">
          <h3>{{ $t('videoHistory.noFilter.title') }}</h3>
          <p>{{ $t('videoHistory.noFilter.desc') }}</p>
          <div class="mc-videohistory-list__no-filter-btn-filter">
            <McButton
              :text="'Filter'"
              variant="outline-primary"
              :icon="'icon-outline_filter'"
              @click="openModalFilter"
            />
          </div>
        </div>
      </div>
    </template>
    <ModalFilter @applyFilter="applyFilter" @loadVehicle="loadVehicle" />
  </div>
</template>

<script>
import HistoryListV2 from './Histories/HistoryListV2'
import responsive from '@/mixin/responsive'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters, mapState } from 'vuex'
import datePickerLang from '@/helpers/date-picker-lang'
import moment from 'moment'
import McButton from '@/components/McButton'
import ModalFilter from './ModalFilter.vue'
import VideoStatusBadge from '@/components/VideoStatusBadge'
import { WAKE_UP_DEVICE } from '@/services/api/onlineCommand'

export default {
  name: 'VideoHistoryListV2',
  components: {
    HistoryListV2,
    McButton,
    ModalFilter,
    VideoStatusBadge,
  },
  mixins: [Helper, responsive, MixPanel],
  data() {
    return {
      inputDate: [],
      loadingVehicle: false,
      sidebarScrolled: false,
    }
  },
  computed: {
    ...mapGetters('vehicle', [
      'vehicleId',
      'vehicleDetail',
      'vehiclePeriod',
      'vehicleVideoHistory',
      'vehicleCameraPosition',
      'vehicleStatusByVehicleId',
    ]),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    ...mapState('vehicle', ['listVehicle']),
    datePickerClasses() {
      return {
        'mc-history-modal__list-date': true,
        'mc-videohistory-button-disabled': this.$store.state.vehicle.isLoadingVehicleVideos,
      }
    },
    datePickerLang() {
      return datePickerLang()
    },
    isVideoDemo() {
      if (!this.$route.query?.vehicleId) {
        return false
      }

      const dataDemoVideoHistory = this.getFeatureFlagValue('vsms2_trackvision2_playback_demo_id')
      return Object.keys(dataDemoVideoHistory).includes(this.$route.query?.vehicleId.toString())
    },
    periodActive() {
      if (!this.vehiclePeriod.length) return ''
      const periodeStart =
        this.momentDateShortMonth(this.vehiclePeriod[0]) +
        ' ' +
        this.momentTime(this.vehiclePeriod[0])
      const periodeEnd = this.momentTime(this.vehiclePeriod[1])
      return periodeStart + ' - ' + periodeEnd + ' ' + this.getLabelTimezone()
    },
    isVideoStatusOnline() {
      const vehicleId = parseInt(this.$route.query?.vehicleId)
      if (!vehicleId || this.listVehicle.length === 0) return false

      const vehicleStatus = this.getVehicleStatus

      if (this.isVideoDemo) return true
      if (!vehicleStatus) return false

      const secondEngineOn = this.getFeatureFlagValue('trackvision_video_offline_acc_on') || 60,
        secondEngineOff = this.getFeatureFlagValue('trackvision_video_offline_acc_off') || 600,
        lastReceive = vehicleStatus.lastReceive,
        engineOn = vehicleStatus.engineOn

      return !this.isDeviceTrackvisionOffline(
        secondEngineOn,
        secondEngineOff,
        lastReceive,
        engineOn
      )
    },
    getVehicleStatus() {
      const vehicleId = this.vehicleId
      if (this.isVideoDemo) {
        return {
          ...this.vehicleStatusByVehicleId(vehicleId),
          calculatedMotionStatus: 'M',
        }
      }
      return this.vehicleStatusByVehicleId(vehicleId)
    },
    isHasAccessSwitchView() {
      return this.hasAccess(['b3n-trackvision-toggle-video-status:read'])
    },
    isNewView() {
      const isNewView = JSON.parse(localStorage.getItem('live-streaming-switch-view-on')) ?? true
      return isNewView
    },
    showBadgeVideoStatus() {
      return (
        (this.hasAccess(['b3j-trackvision-video-history-status:read']) &&
          !this.isHasAccessSwitchView) ||
        (this.isHasAccessSwitchView && this.isNewView)
      )
    },
    isMdvrSleep() {
      return (
        this.isDeviceBsjMdvr(this.vehicleDetail.deviceCode) &&
        this.getVehicleStatus.calculatedMotionStatus === 'P'
      )
    },
  },

  async created() {
    this.$store.commit('vehicle/clearPeriod')
  },
  async mounted() {
    try {
      if (!this.vehicleId) return

      await this.initLoadData(this.vehicleId)

      while (!this.getVehicleStatus) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }

      if (this.isVideoDemo) {
        this.initLoadVehicleVideosDemo()
      } else {
        this.initLoadVehicleVideos()
      }
    } catch (error) {
      this.$mcToast.error(error?.message ?? this.$t('global.somethingWentWrong'))
      console.log(error)
    }
  },
  methods: {
    getPeriodFromRouteQuery() {
      const { startTime, endTime } = this.$route.query
      if (!startTime || !endTime) return null

      const startDate = new Date(startTime)
      const endDate = new Date(endTime)

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null
      if (startDate.getTime() >= endDate.getTime()) return null

      return [startDate.toISOString(), endDate.toISOString()]
    },
    async initLoadData(vehicleId) {
      const periodFromQuery = this.getPeriodFromRouteQuery()
      const periode = periodFromQuery || [
        this.formatDateToUtc(moment(new Date()).format('YYYY-MM-DD 00:00:00')),
        this.formatDateToUtc(moment(new Date()).format('YYYY-MM-DD HH:mm:59')),
      ]
      this.updatePeriodState(periode)
      await this.loadVehicle(vehicleId)
    },

    async sendOnlineCommandWakeup() {
      const imei = this.vehicleDetail.imei
      await this.$apollo.mutate({
        mutation: WAKE_UP_DEVICE,
        variables: {
          input: {
            imei,
          },
        },
      })
    },

    updatePeriodState(periode) {
      this.$store.commit('vehicle/setVehicleHistoryPeriod', periode)
    },

    async loadVehicle(vehicleId) {
      this.loadingVehicle = true
      await this.$store.dispatch('vehicle/requestVehicleVideoTrackerByVehicleId', vehicleId)
      this.loadingVehicle = false
    },

    async requestFileList() {
      if (!this.vehicleDetail?.imei) return
      await this.$store.dispatch('vehicle/requestUpdateFileList', {
        input: {
          imei: this.vehicleDetail.imei,
        },
      })
    },

    async requestVehicleVideos(vehicleId, isFromCache) {
      this.$store.commit('vehicle/clearVideoStates')
      const filter = {
        vehicleId: vehicleId,
        startTime: this.vehiclePeriod[0],
        endTime: this.vehiclePeriod[1],
        channels: [
          this.$store.state.vehicle.vehicleDetail.deviceName === 'jc'
            ? this.$store.state.vehicle.channelActive + 1
            : this.$store.state.vehicle.channelActive,
        ],
        fileListOnly: true,
        isFromCache,
      }
      const response = await this.$store.dispatch('vehicle/requestVehicleVideosTv2', filter)
      return response
    },

    async initLoadVehicleVideosDemo() {
      this.$store.commit('vehicle/setLoadingVehicleVideos', true)
      await this.delay(500)
      if (this.vehiclePeriod.length === 0) {
        this.vehiclePeriod[0] = moment(new Date()).format('YYYY-MM-DD 00:00:00')
        this.vehiclePeriod[1] = moment(new Date()).format('YYYY-MM-DD HH:mm:59')
      }
      this.$store.dispatch('vehicle/setVehicleVideosDemoV2', {
        vehicleId: this.$route.query.vehicleId.toString(),
        channel: this.$store.state.vehicle.channelActive,
      })
      this.$store.commit('vehicle/setLoadingVehicleVideos', false)
    },

    async initLoadVehicleVideos() {
      this.$store.commit('vehicle/setLoadingVehicleVideos', true)

      if (this.isMdvrSleep) {
        await this.sendOnlineCommandWakeup()
        await this.delay(60 * 1000)
      }

      await this.requestFileList()
      if (
        this.$store.state.vehicle.vehicleDetail.deviceName === 'bsj' ||
        this.$store.state.vehicle.vehicleDetail.deviceName === 'jc'
      ) {
        await this.delay(5000)
      }

      const response = await this.requestVehicleVideos(this.vehicleId, false)

      this.$store.dispatch('vehicle/splitVehicleVideos', {
        videoFiles: response.files,
        lastUpdate: response.lastUpdate,
        filter: {
          startTime: this.vehiclePeriod[0],
          endTime: this.vehiclePeriod[1],
        },
      })
      this.$store.commit('vehicle/setLoadingVehicleVideos', false)
    },

    openModalFilter() {
      this.$bvModal.show('modal-filter-videohistory-trackvision')
    },
    async applyFilter() {
      await this.$nextTick()
      if (this.isVideoDemo) {
        this.initLoadVehicleVideosDemo()
      } else {
        this.initLoadVehicleVideos()
      }
    },
    handleScrollSidebar(e) {
      if (e.target.scrollTop >= 20 && !this.sidebarScrolled) {
        this.sidebarScrolled = true
      } else if (e.target.scrollTop < 20 && this.sidebarScrolled) {
        this.sidebarScrolled = false
      }
    },
  },
}
</script>
